import { Component, computed, inject, input, output, signal } from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';

import { AuthService } from '@services/auth.service';
import { getUserInitials } from '@utilities/initials';
import { humaniseId } from '@utilities/labels';

interface NavItem {
  readonly path: string;
  readonly label: string;
  readonly icon: string;
  readonly roles?: readonly string[];
}

interface NavSection {
  readonly title: string;
  readonly items: readonly NavItem[];
}

@Component({
  selector: 'app-sidebar',
  imports: [NgOptimizedImage, RouterLink, RouterLinkActive],
  templateUrl: './app-sidebar.html',
  styleUrl: './app-sidebar.css',
  host: { class: 'sidebar' },
})
export class AppSidebar {
  private readonly auth = inject(AuthService);

  readonly collapsed = input(false);

  readonly toggleCollapse = output<void>();
  readonly navigated = output<void>();

  protected readonly filter = signal('');

  private readonly user = this.auth.user;

  protected readonly userName = computed(() => {
    const user = this.user();
    return user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : 'Signed out';
  });

  protected readonly userInitials = computed(() => {
    const user = this.user();
    return user ? getUserInitials(user.firstName, user.lastName) : '';
  });

  protected readonly userRole = computed(() => humaniseId(this.auth.roles()[0]));

  private readonly sections: readonly NavSection[] = [
    {
      title: 'Overview',
      items: [
        { path: '/a/dashboard', label: 'Dashboard', icon: 'squares-four' },
        { path: '/a/transactions', label: 'Transactions', icon: 'arrows-left-right' },
        { path: '/a/statements', label: 'Statements', icon: 'file-text' },
      ],
    },
    {
      title: 'Clients',
      items: [
        { path: '/a/clients', label: 'Clients', icon: 'buildings' },
        { path: '/a/client-types', label: 'Client Types', icon: 'tag' },
        { path: '/a/payment-methods', label: 'Payment Methods', icon: 'wallet' },
        { path: '/a/users', label: 'Users', icon: 'users-three' },
      ],
    },
    {
      title: 'Partners',
      items: [
        { path: '/a/partners', label: 'Partners', icon: 'handshake' },
        { path: '/a/credentials', label: 'API Credentials', icon: 'key' },
        { path: '/a/accounts', label: 'Ledger Accounts', icon: 'bank' },
      ],
    },
    {
      title: 'Pricing',
      items: [
        { path: '/a/charges', label: 'Charges', icon: 'percent' },
        { path: '/a/channels', label: 'Channels', icon: 'broadcast' },
        { path: '/a/transaction-types', label: 'Transaction Types', icon: 'list-checks' },
      ],
    },
    {
      title: 'Reference',
      items: [
        { path: '/a/currencies', label: 'Currencies', icon: 'currency-circle-dollar' },
        { path: '/a/banks', label: 'Banks', icon: 'bank' },
      ],
    },
  ];

  protected readonly supportItems: readonly NavItem[] = [
    { path: '/a/portal-users', label: 'Portal Users', icon: 'user-gear' },
    { path: '/a/configuration', label: 'Configuration', icon: 'sliders' },
  ];

  protected readonly visibleSupportItems = computed(() =>
    this.supportItems.filter((item) => this.auth.hasAnyRole(item.roles ?? [])),
  );

  protected readonly visibleSections = computed(() => {
    const term = this.filter().trim().toLowerCase();

    return this.sections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => this.auth.hasAnyRole(item.roles ?? [])),
      }))
      .map((section) => {
        if (!term) return section;
        if (section.title.toLowerCase().includes(term)) return section;
        return {
          ...section,
          items: section.items.filter((item) => item.label.toLowerCase().includes(term)),
        };
      })
      .filter((section) => section.items.length > 0);
  });

  protected onFilterInput(value: string): void {
    this.filter.set(value);
  }
}
